import { useEffect, useRef, useState } from 'react'
import { ApiError, verifyEmail } from '@features/account/api/verifyEmailApi'
import { Icon } from '@/icons/Icon'
import { useTranslation } from '@/i18n'

/**
 * `#verify-email/<token>` 用ページ (T-H2、B5 でデザイン刷新)。
 *
 * - mount で 1 度だけ POST /auth/verify-email を呼ぶ
 * - 状態 (loading / success / invalid / network) ごとに 1 枚のカードで
 *   アイコン + タイトル + 説明 + 単一ボタンを表示するシンプルなレイアウト
 */

export interface VerifyEmailPageProps {
  /** URL から抽出した raw token (App.tsx 側で `verify-email/<token>` を分解して渡す)。 */
  token: string
}

type VerifyState = 'loading' | 'success' | 'invalid' | 'network'

const ICON_FOR: Record<VerifyState, 'mail' | 'check' | 'alert'> = {
  loading: 'mail',
  success: 'check',
  invalid: 'alert',
  network: 'alert',
}

const goLogin = (): void => {
  window.location.hash = 'login'
}

export const VerifyEmailPage = ({ token }: VerifyEmailPageProps): JSX.Element => {
  const { t } = useTranslation()
  const [state, setState] = useState<VerifyState>('loading')

  // verify-email トークンの消費は **非冪等** (2 回目以降は 400 になる)。
  // React 18 StrictMode は effect を 2 回起動するため、token ごとに 1 度だけ
  // API を呼ぶよう ref でガードする。`cancelled` フラグは使わない — それだと
  // StrictMode の cleanup が唯一の実 API 呼び出しの結果を握りつぶし、
  // loading のまま固まってしまう。setState はアンマウント後でも React 18 では no-op。
  const requestedTokenRef = useRef<string | null>(null)

  useEffect(() => {
    if (requestedTokenRef.current === token) return
    requestedTokenRef.current = token

    const run = async (): Promise<void> => {
      try {
        await verifyEmail(token)
        setState('success')
      } catch (err: unknown) {
        setState(err instanceof ApiError && err.status === 400 ? 'invalid' : 'network')
      }
    }

    void run()
  }, [token])

  const titleKey =
    state === 'loading'
      ? 'auth.verify_email.loading_title'
      : state === 'success'
        ? 'auth.verify_email.success_title'
        : state === 'invalid'
          ? 'auth.verify_email.error_invalid_title'
          : 'auth.verify_email.error_network_title'

  const messageKey =
    state === 'loading'
      ? 'auth.verify_email.loading'
      : state === 'success'
        ? 'auth.verify_email.success'
        : state === 'invalid'
          ? 'auth.verify_email.error_invalid'
          : 'auth.verify_email.error_network'

  return (
    <div className="verify-email-stage" data-testid="verify-email-page">
      <div className="verify-email-card" data-state={state}>
        <div className="verify-email-icon" aria-hidden="true">
          <Icon name={ICON_FOR[state]} />
        </div>

        <h1 className="verify-email-title">{t(titleKey)}</h1>
        <p className="verify-email-msg">{t(messageKey)}</p>

        {state !== 'loading' && (
          <button
            type="button"
            className="btn btn-primary verify-email-btn"
            onClick={goLogin}
            data-testid="verify-email-go-login"
          >
            {t('auth.verify_email.go_login')}
          </button>
        )}
      </div>
    </div>
  )
}
